import PropTypes from 'prop-types'
import {
  createContext,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { debounce, isPresent } from 'utils'

const CenterContext = createContext({
  height: 0,
  width: 0,
  isSet: false,
})

const Container = forwardRef(
  ({ tag: TagName, children, style = {}, on = true, ...attrs }, outerRef) => {
    const innerRef = useRef()
    const ref = outerRef || innerRef
    const [dimension, setDimension] = useState({
      height: 0,
      width: 0,
      isSet: false,
    })

    if (!TagName) throw new TypeError("'tag' prop is required.")

    const updateDimension = useCallback(() => {
      if (on && isPresent(ref.current)) {
        const { height, width } = ref.current.getBoundingClientRect()
        if (dimension.height !== height || dimension.width !== width) {
          setDimension({
            height,
            width,
            isSet: true,
          })
        }
      }
    }, [on, ref, dimension.height, dimension.width])

    useEffect(() => {
      updateDimension()

      const debouncedUpdateDimension = debounce(updateDimension, 200)
      window.addEventListener('resize', debouncedUpdateDimension)

      const observer = new MutationObserver(updateDimension)
      observer.observe(ref.current, {
        attributes: true,
        childList: true,
        subtree: true,
      })

      return () => {
        window.removeEventListener('resize', debouncedUpdateDimension)
        observer.disconnect()
      }
    }, [updateDimension, ref])

    return (
      <CenterContext.Provider value={dimension}>
        <TagName
          ref={ref}
          style={{ position: 'relative', ...style }}
          {...attrs}>
          {children}
        </TagName>
      </CenterContext.Provider>
    )
  }
)

Container.displayName = 'Center.Container'
Container.propTypes = {
  tag: PropTypes.string.isRequired,
  children: PropTypes.node,
  style: PropTypes.object,
  on: PropTypes.bool,
}

const Content = memo(
  forwardRef(({ attrs, top, left, style, children, TagName }, ref) => (
    <TagName
      ref={ref}
      {...attrs}
      style={{
        position: 'absolute',
        top,
        left,
        ...style,
      }}>
      {children}
    </TagName>
  ))
)
Content.displayName = 'Content'
Content.propTypes = {
  attrs: PropTypes.object,
  top: PropTypes.number,
  left: PropTypes.number,
  style: PropTypes.object,
  children: PropTypes.node,
  TagName: PropTypes.string,
}

const Center = ({
  tag: TagName = 'div',
  children,
  offsetX = 0,
  offsetY = 0,
  limit = true,
  style = {},
}) => {
  if (!TagName) throw new TypeError("'tag' prop is required.")

  const ref = useRef()
  const { height, width, isSet } = useContext(CenterContext)
  const [position, setPosition] = useState({
    top: Number.MIN_SAFE_INTEGER,
    left: Number.MIN_SAFE_INTEGER,
  })

  const updatePosition = useCallback(() => {
    setPosition(oldPosition => {
      const { height: childHeight, width: childWidth } =
        ref.current.getBoundingClientRect()
      let top = (height - childHeight) / 2 + offsetY
      let left = (width - childWidth) / 2 + offsetX

      if (limit) {
        top = Math.max(Math.min(height - childHeight, top), 0)
        left = Math.max(Math.min(width - childWidth, left), 0)
      }

      if (oldPosition.top !== top || oldPosition.left !== left) {
        if (childHeight > 0 && childWidth > 0 && height > 0 && width > 0) {
          return {
            top,
            left,
          }
        }
      }
      return oldPosition
    })
  }, [setPosition, height, width, limit, offsetX, offsetY])

  useEffect(() => {
    if (!isSet) return
    updatePosition()
    const observer = new MutationObserver(updatePosition)
    observer.observe(ref.current, {
      attributes: true,
      childList: true,
      subtree: true,
    })
    return () => observer.disconnect()
  }, [ref, isSet, updatePosition])

  return (
    <Content
      ref={ref}
      top={position.top}
      left={position.left}
      style={style}
      TagName={TagName}>
      {children}
    </Content>
  )
}

Center.Container = Container
Center.propTypes = {
  tag: PropTypes.string,
  children: PropTypes.node,
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  limit: PropTypes.bool,
  style: PropTypes.object,
}

export default Center
